import React from "react";
import { Navigate } from "react-router-dom";
import Auth from "../components/Auth/Auth";
import LabelPreview from "../components/PDF/TooltipLabelPreview";
import UserProfile from "../components/Users/Profile/UserProfile";
import MultiplePDF from "../components/PDF/MultiplePDF/MultiplePDF";
import Selections from "../components/Selections/Selections";
import Selection from "../components/Selections/Selection/Selection";
import Layout from "../components/Layout";
import CustomersTable from "../components/Customers/CustomersTable/CustomersTable";
import MixturesTable from "../components/Mixtures/MixturesTable";
import CustomerOverview from "../components/Customers/CustomerOverview/CustomerOverview";
import Breadcrumbs from "../components/Common/Breadcrumbs/Breadcrumbs";
import CapsuleDisplay from "../components/CapsuleWorkflow/CapsuleDisplay/CapsuleDisplay";
import CapsulesList from "../components/CapsuleWorkflow/CapsulesList/CapsulesList";
import { useAuth } from "../context/AuthContext";
import { BreadcrumbsProvider } from "../context/BreadcrumbsContext";
import Encoder from "../components/Encoder/Encoder";
import VerifyAccount from "../components/Auth/VerifyAccount";
import CapsuleReview from "../components/Capsules/CapsuleReview";
import Orders from "../components/Users/Profile/Orders/Orders";
import UserInfo from "../components/Users/Profile/UserInfo";
import TransactionsTable from "../components/Users/Profile/Transactions/TransactionsTable";
import Products from "../components/Products/Products";
import { CartProvider } from '../context/CartContext';
import NewOrderPage from "../pages/Orders/NewOrderPage";
import SelectionOrderPage from "../pages/Orders/SelectionOrderPage";
import AllCapusle from "../pages/Capsules/AllCapusle";
import NotFound from "../components/Common/NotFound/NotFound";
import AdminDashboard from "../components/Admin/AdminDashboard";
import TherapistsManagement from "../components/Admin/Therapists/TherapistsManagement";
import CapsulesManagement from "../components/Admin/Capsules/CapsulesManagement";
import MixturesManagement from "../components/Admin/Mixtures/MixturesManagement";
import CustomersManagement from "../components/Admin/Customers/CustomersManagement";
import PopupConfigurationPage from "../pages/Admin/PopupConfigurationPage";

const renderWithLayout = (element) => <Layout>{element}</Layout>;

const renderWithBreadcrumbsOnly = (element) => (
  <BreadcrumbsProvider>
    <div className="w-full">
      <Breadcrumbs />
      {element}
    </div>
  </BreadcrumbsProvider>
);

const renderWithLayoutAndBreadcrumbs = (element) => (
  <Layout>
    <Breadcrumbs />
    {element}
  </Layout>
);

const ProtectedRoute = ({ element }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center w-full items-center h-screen">
        <div className={`loading loading-spinner text-primary w-32 h-32`}></div>
      </div>
    )
  }

  return isAuthenticated ? element : <Navigate to="/se-connecter" replace />;
};

const createRoute = (path, element, isProtected = true, useBreadcrumbs = false, useLayout = true) => {
  let wrappedElement = element;

  // First wrap with CartProvider if it's the products route
  if (path === '/produits') {
    wrappedElement = <CartProvider>{element}</CartProvider>;
  }

  // Then apply the other wrappers
  if (useBreadcrumbs && !useLayout) {
    wrappedElement = renderWithBreadcrumbsOnly(wrappedElement);
  } else if (useBreadcrumbs && useLayout) {
    wrappedElement = renderWithLayoutAndBreadcrumbs(wrappedElement);
  } else if (useLayout) {
    wrappedElement = renderWithLayout(wrappedElement);
  }

  return {
    path,
    element: isProtected ? <ProtectedRoute element={wrappedElement} /> : wrappedElement,
  };
};

const layoutRoutes = {
  root: {
    path: "/",
    element: <Navigate to="/clients" replace />
  },
  products: createRoute("/produits", <Products />, true, true),
  customers: {
    path: "/clients",
    children: [
      createRoute("", <CustomersTable />, true, true, true),
      {
        path: ":userId",
        element: createRoute("", <CustomerOverview />, true, true, true).element,
        children: [
          createRoute("", <CapsulesList />, true, false, false),
          createRoute("capsule", <CapsulesList />, true, false, false),
          createRoute("capsule/:capsuleId", <CapsuleDisplay />, true, false, false),
          createRoute("commander", <NewOrderPage />, true, false, false),
        ],
      },
    ],
  },
  mixtures: {
    path: "/melanges",
    children: [
      {
        path: "",
        element: <Navigate to="/melanges/catalogue" />,
      },
      createRoute("personnalises", <MixturesTable type={"custom"} />, true, true),
      createRoute("catalogue", <MixturesTable type={"catalogue"} />, true, true),
    ],
  },
  labelPDF: createRoute("/labelPDF", <LabelPreview />, true, true),
  generatePDF: createRoute("/pdf/multiple", <MultiplePDF />, true, true),
  selections: createRoute("/selections", <Selections />, true, true),
  capsules: createRoute("/mes-capsules", <AllCapusle />, true, true),
  selectionOrderNew: createRoute("/commander-selection", <SelectionOrderPage />, true, true, true),
  myAccount: {
    path: "/mon-compte",
    element: createRoute("", <UserProfile />, true, true).element,
    children: [
      createRoute("", <Navigate to={'/mon-compte/commandes'} />, true, false, false),
      createRoute("commandes", <Orders />, true, false, false),
      createRoute("transactions", <TransactionsTable />, true, false, false),
      createRoute("informations", <UserInfo />, true, false, false),
      createRoute("parametres", <><p>Order Detail</p></>, true, false, false),
    ],
  },
  admin: {
    path: "/admin",
    element: createRoute("", <AdminDashboard />, true, true).element,
    children: [
      createRoute("", <Navigate to={'/admin/capsules'} />, true, false, false),
      createRoute("therapeutes", <TherapistsManagement />, true, false, false),
      createRoute("clients", <CustomersManagement />, true, false, false),
      createRoute("capsules", <CapsulesManagement />, true, false, false),
      createRoute("melanges", <MixturesManagement />, true, false, false),
      createRoute("selections", <Selections />, true, false, false),
      createRoute("popups", <PopupConfigurationPage />, true, false, false),
    ],
  },
};

const noLayoutRoutes = {
  encodeMixture: createRoute("/encode/melange/:id", <Encoder type="mixture" />, true, false, false),
  encodeConfiguration: createRoute("/encode/configuration/:id", <Encoder type="configuration" />, true, false, false),
  encodeCapsule: createRoute("/encode/capsules/:id", <Encoder type="capsule" />, true, false, false),
  login: createRoute("/se-connecter", <Auth />, false, false, false),
  signup: createRoute("/inscription", <Auth />, false, false, false),
  forgotPassword: createRoute("/mot-de-passe-oublie", <Auth />, false, false, false),
  verifyAccount: createRoute("/verification-compte", <VerifyAccount />, false, false, false),
  resetPassword: createRoute("/reinitialiser-mot-de-passe", <Auth />, false, false, false),
  oneSelection: createRoute("/selections/:id", <Selection />, true, false, false),
  selectionOrder: createRoute("/selections/commander/:selectionId", <SelectionOrderPage />, true, false, false),
  capsuleReview: createRoute("/capsule/review", <CapsuleReview />, false, false, false),
  notFound: createRoute("*", <NotFound />, false, false, false),
};

const routes = { ...layoutRoutes, ...noLayoutRoutes };

export const ROUTES = Object.values(routes);
